import { useCallback, useEffect, useRef } from 'react';
import { fg } from '@confluence/feature-gating';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser';

const useLazyAmplitudeInit = () => {
	return useCallback(async () => {
		if (!fg('cc_engagement_amplitude_session_replay_gate')) {
			return; // no-op
		}
		const module = await import(
			/* webpackChunkName: "loadable-AmplitudeSessionReplay" */ './AmplitudeSessionReplay'
		);
		return module.AmplitudeSessionReplay.init();
	}, []);
};

const useLazyAmplitudeStop = () => {
	return useCallback(async () => {
		if (!fg('cc_engagement_amplitude_session_replay_gate')) {
			return; // no-op
		}
		const module = await import(
			/* webpackChunkName: "loadable-AmplitudeSessionReplay" */ './AmplitudeSessionReplay'
		);
		return module.AmplitudeSessionReplay.stop();
	}, []);
};

export const useAmplitudeSessionReplay = ({
	skip = false,
	timeoutMs = 180000, // 3 minutes default
} = {}) => {
	const amplitudeInit = useLazyAmplitudeInit();
	const amplitudeStop = useLazyAmplitudeStop();
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const isInitializedRef = useRef(false);
	const analyticsEventFiredRef = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isNewUser, loading: isLoadingNewUser } = useIsNewUser(
		{},
		skip || !fg('cc_engagement_amplitude_session_replay_gate'),
	);

	useEffect(() => {
		if (skip || !fg('cc_engagement_amplitude_session_replay_gate')) {
			return;
		}

		// Initialize amplitude
		void amplitudeInit();
		isInitializedRef.current = true;

		// Set timeout to stop after specified time
		timeoutRef.current = setTimeout(() => {
			void amplitudeStop();
			isInitializedRef.current = false;
		}, timeoutMs);

		// Cleanup function
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (isInitializedRef.current) {
				void amplitudeStop();
				isInitializedRef.current = false;
			}
		};
	}, [skip, amplitudeInit, amplitudeStop, timeoutMs]);

	useEffect(() => {
		if (
			skip ||
			!fg('cc_engagement_amplitude_session_replay_gate') ||
			!isInitializedRef.current ||
			analyticsEventFiredRef.current ||
			isLoadingNewUser
		) {
			return;
		}

		if (isNewUser) {
			//Use this analytics event to add extra attributes so it can be filtered in Amplitude
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'triggered',
					actionSubject: 'amplitudeSessionReplay',
					source: 'confluenceAmplitudeSessionReplay',
					attributes: {
						isNewUser: true,
					},
				},
			}).fire();
			analyticsEventFiredRef.current = true;
		}
	}, [skip, isNewUser, createAnalyticsEvent, isLoadingNewUser]);

	// Cleanup effect that always runs on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (isInitializedRef.current) {
				void amplitudeStop();
				isInitializedRef.current = false;
			}
		};
	}, [amplitudeStop]);

	// Handle pagehide to stop amplitude session replay when page closes
	useEffect(() => {
		if (skip || !fg('cc_engagement_amplitude_session_replay_gate')) {
			return;
		}

		const handlePageHide = () => {
			if (isInitializedRef.current) {
				void amplitudeStop();
				isInitializedRef.current = false;
			}
		};

		window.addEventListener('pagehide', handlePageHide);

		return () => {
			window.removeEventListener('pagehide', handlePageHide);
		};
	}, [skip, amplitudeStop]);
};
